import { ReactElement } from 'react'
import type {
  ShearAnchorCheckDTO,
  ShearAnchorConnectorSelectionDTO,
  ShearAnchorLoadsDTO,
  ShearAnchorPosition,
} from '@api/fastAPI.schemas'
import CommentDrawer from '@editor/components/CommentDrawer'
import { useEditorViewStore } from '@editorStores'
import { MultiEdit } from '../../../StructuralPlanning/components'
import Results2AnchorDetailPane from '../Results2AnchorDetailPane'
import Results2AnchorMultiEditPane from '../Results2AnchorMultiEditPane'
import Results2AssemblySurfaceDetailPane from '../Results2AssemblySurfaceDetailPane'
import Results2DetailPane from '../Results2DetailPane'
import type { Results2ListItem } from '../Results2ElementList'
import Results2EmptyDetailPane from '../Results2EmptyDetailPane'
import Results2ShearAnchorDetailPane from '../Results2ShearAnchorDetailPane'
import Results2ShearAnchorMultiEditPane from '../Results2ShearAnchorMultiEditPane'
import Results2ShearAnchorSelectionPane from '../Results2ShearAnchorSelectionPane'
import Results2StandardRipDetailPane from '../Results2StandardRipDetailPane'
import Results2StiffeningDetailPane from '../Results2StiffeningDetailPane'

type DetailPaneProps = {
  showNoData: boolean
  noDataMessage: string

  /**
   * While results are (re)loading the result store is cleared to null (see
   * `clearResults` on "Berechnen"). Gate the data panes on this so they render an
   * empty/loading state instead of reading the now-null result arrays and crashing.
   */
  isLoading: boolean

  resolvedSelectedId?: string
  selectedItem?: Results2ListItem

  groupingEnabled: boolean
  positionGrouping?: MemberPositionBundle[] | null
  positionGroupingType?: PositionGroupingType | null
  isMultiEditMode: boolean
  selectedElementIds: string[]
  /** Anchors selected in the "Anker" list — several of them open the anchor multi-edit pane. */
  selectedAnchorItems?: Results2ListItem[]
  selectedShearAnchorItems?: Results2ListItem[]
  resetSelectedElements: () => void

  isStiffeningTab: boolean
  isStandardRipsTab: boolean
  isAnchorsTab?: boolean
  isShearAnchorsTab?: boolean
  isWallAssemblyTab?: boolean
  isAssemblySurfaceTab?: boolean
  /** Foundations carry no design checks, so the detail pane stays empty for them. */
  isFoundationTab?: boolean
  selectedStiffeningChecks: StiffeningSegmentCheck[]
  /** Which of the selected segment's two shear anchors (foot or head) is selected. */
  selectedShearAnchorPosition?: ShearAnchorPosition
  /** Shear anchor checks of the selected stiffening segment (one per load category and position). */
  selectedShearAnchorChecks: ShearAnchorCheckDTO[]
  /** The characteristic loads the selected segment's shear flow is derived from. */
  selectedShearAnchorLoads?: ShearAnchorLoadsDTO
  selectedShearAnchorSelections?: ShearAnchorConnectorSelectionDTO[]

  /** When the "Kommentare" list tab is active, the detail pane shows the comment settings drawer. */
  isCommentsTab?: boolean

  /** When true, disables detail form edits (Bemessung fields, save, documents upload, …). */
  disableForm?: boolean
}

const DetailPane = ({
  showNoData,
  noDataMessage,
  isLoading,
  resolvedSelectedId,
  selectedItem,
  groupingEnabled,
  positionGrouping,
  positionGroupingType,
  isMultiEditMode,
  selectedElementIds,
  selectedAnchorItems = [],
  selectedShearAnchorItems = [],
  resetSelectedElements,
  isStiffeningTab,
  isStandardRipsTab,
  isAnchorsTab = false,
  isShearAnchorsTab = false,
  isWallAssemblyTab = false,
  isAssemblySurfaceTab = false,
  isFoundationTab = false,
  selectedStiffeningChecks,
  selectedShearAnchorPosition,
  selectedShearAnchorChecks,
  selectedShearAnchorLoads,
  selectedShearAnchorSelections = [],
  isCommentsTab = false,
  disableForm = false,
}: DetailPaneProps): ReactElement => {
  const shearAnchorSelectionOpen = useEditorViewStore(
    state => state.results.shearAnchorSelectionOpen,
  )

  if (isCommentsTab) {
    return <CommentDrawer />
  }

  // Before the no-data/loading guards: the relevant anchors are configured per storey,
  // not per result, so the panel must open before anything was computed. A selected
  // segment's detail pane wins; the panel comes back when the selection is closed.
  if (isShearAnchorsTab && shearAnchorSelectionOpen && !resolvedSelectedId) {
    return <Results2ShearAnchorSelectionPane />
  }

  if (showNoData) {
    return <Results2EmptyDetailPane emptyMessage={noDataMessage} />
  }

  // Results are cleared to null while (re)computing; show a loading state instead of
  // mounting the data panes, which would read the now-null result arrays and crash.
  if (isLoading) {
    return (
      <Results2EmptyDetailPane
        selectedItem={selectedItem}
        emptyMessage="Ergebnisse werden geladen …"
      />
    )
  }

  if (isAnchorsTab && selectedAnchorItems.length > 1) {
    return (
      <Results2AnchorMultiEditPane selectedItems={selectedAnchorItems} disableForm={disableForm} />
    )
  }

  if (isShearAnchorsTab && selectedShearAnchorItems.length > 1) {
    return (
      <Results2ShearAnchorMultiEditPane
        selectedItems={selectedShearAnchorItems}
        disableForm={disableForm}
      />
    )
  }

  if (
    (groupingEnabled || isWallAssemblyTab || isAssemblySurfaceTab) &&
    isMultiEditMode &&
    selectedElementIds.length > 1
  ) {
    // Multi-edit must run before the single-selection empty state: scene/marquee selection order
    // can leave `selectedElementIds[0]` missing from the current list while other ids are valid.
    return <MultiEdit selectedElements={selectedElementIds} resetElements={resetSelectedElements} />
  }

  if (!selectedItem) {
    return <Results2EmptyDetailPane emptyMessage="Bitte ein Element auswählen" />
  }

  if (isAssemblySurfaceTab || isWallAssemblyTab || isFoundationTab) {
    const assemblyElementId = resolvedSelectedId ?? selectedItem.id
    return (
      <Results2AssemblySurfaceDetailPane
        key={assemblyElementId}
        elementId={assemblyElementId}
        selectedItem={selectedItem}
        disableForm={disableForm}
      />
    )
  }

  if (isAnchorsTab && resolvedSelectedId) {
    return (
      <Results2AnchorDetailPane
        key={resolvedSelectedId}
        supportGuid={resolvedSelectedId}
        selectedItem={selectedItem}
        disableForm={disableForm}
      />
    )
  }

  if (isShearAnchorsTab && resolvedSelectedId && selectedShearAnchorPosition) {
    return (
      <Results2ShearAnchorDetailPane
        key={`${resolvedSelectedId}-${selectedShearAnchorPosition}`}
        segmentGuid={resolvedSelectedId}
        position={selectedShearAnchorPosition}
        checks={selectedShearAnchorChecks}
        loads={selectedShearAnchorLoads}
        selections={selectedShearAnchorSelections}
        disableForm={disableForm}
      />
    )
  }

  if (isStiffeningTab) {
    return (
      <Results2StiffeningDetailPane selectedItem={selectedItem} checks={selectedStiffeningChecks} />
    )
  }

  if (isStandardRipsTab && resolvedSelectedId) {
    return (
      <Results2StandardRipDetailPane
        key={resolvedSelectedId}
        elementId={resolvedSelectedId}
        selectedItem={selectedItem}
        disableForm={disableForm}
      />
    )
  }

  return (
    <Results2DetailPane
      elementId={resolvedSelectedId as string}
      elementLabel={selectedItem?.label}
      elementSubtitle={
        selectedItem ? `${selectedItem.type} · ${selectedItem.qs}` : 'Kein Bauteil ausgewählt'
      }
      accepted={selectedItem?.bundleExported}
      isChildElement={!!selectedItem?.parent}
      positionGrouping={positionGrouping}
      positionGroupingType={positionGroupingType}
      disableForm={disableForm}
    />
  )
}

export default DetailPane
